import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { StudyAreaCard } from './StudyAreaCard'
import { getCategoriesSorted, CategoryDisplay } from '../config/categoryConfig'
import { DataLayer } from '../lib/dataLayer'
import { ExerciseCategory } from '../types/database'

interface CategoryStats {
  totalCards: number
  masteredCards: number
  exerciseCount: number
}

interface HomeViewProps {
  onSelectArea: (areaId: string) => void
}

export function HomeView({ onSelectArea }: HomeViewProps) {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [categoryStats, setCategoryStats] = useState<Map<ExerciseCategory, CategoryStats>>(new Map())
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadData() {
      if (!user) return

      try {
        setLoading(true)
        setError(null)

        const [exercises, cards, progress] = await Promise.all([
          DataLayer.getExercises(),
          DataLayer.getCards(),
          DataLayer.getUserCardProgress(user.id),
        ])

        const progressMap = new Map(progress.map(p => [p.card_id, p]))
        const statsMap = new Map<ExerciseCategory, CategoryStats>()

        for (const cat of getCategoriesSorted()) {
          const catExercises = exercises.filter(e => e.category === cat.id)
          const catExerciseIds = new Set(catExercises.map(e => e.id))
          const catCards = cards.filter(c => c.exercise_id && catExerciseIds.has(c.exercise_id))

          let mastered = 0
          for (const card of catCards) {
            const p = progressMap.get(card.id)
            if (p && p.status === 'mastered') mastered++
          }

          statsMap.set(cat.id, {
            totalCards: catCards.length,
            masteredCards: mastered,
            exerciseCount: catExercises.length,
          })
        }

        setCategoryStats(statsMap)
      } catch (err) {
        console.error('Failed to load home data:', err)
        setError('Failed to load study areas')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [user])

  const categories = getCategoriesSorted()

  if (error) {
    return (
      <div className="max-w-3xl mx-auto text-center py-12">
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Study Areas</h1>
        <p className="text-gray-600 mt-1">Choose an area to focus your study session</p>
      </div>

      <div className="space-y-4">
        {categories.map((cat: CategoryDisplay) => {
          const stats = categoryStats.get(cat.id)
          return (
            <StudyAreaCard
              key={cat.id}
              category={cat}
              stats={stats || null}
              loading={loading}
              onClick={() => onSelectArea(cat.id)}
            />
          )
        })}
      </div>
    </div>
  )
}
